import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminUser } from '@central/contracts';
import { Button } from '@/components/Button';
import {
  alterarOperador,
  apagarOperador,
  atribuirPapel,
  chavesDeAdministracao,
  criarOperador,
  listarOperadores,
  listarPapeis,
  revogarPapel,
} from '@/features/admin/admin.api';
import {
  AcoesDaLinha,
  Cabecalho,
  Campo,
  CascaDeCatalogo,
  Celula,
  CLASSE_DE_CAMPO,
  ConfirmacaoDeCatalogo,
  EtiquetaDeAtividade,
  Linha,
  LinhaDeCabecalho,
  useEscritaDeCatalogo,
  type Intencao,
} from '@/features/admin/componentes';
import { Modal } from '@/features/tickets/Modal';

const STATUS_ROTULO: Record<AdminUser['status'], string> = {
  ACTIVE: 'Ativo',
  INVITED: 'Convidado',
  SUSPENDED: 'Suspenso',
  DEACTIVATED: 'Desativado',
  PENDING_APPROVAL: 'Aguardando aprovação',
};

export function UsuariosPage() {
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.operadores,
    queryFn: listarOperadores,
  });
  const operadores = consulta.data?.items ?? [];

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<AdminUser | null>(null);
  const [confirmando, setConfirmando] = useState<{
    registro: AdminUser;
    intencao: Intencao;
  } | null>(null);

  const atividade = useEscritaDeCatalogo(
    ({ id, status, version }: { id: string; status: AdminUser['status']; version: number }) =>
      alterarOperador(id, { status, version }),
    () => setConfirmando(null),
  );
  const remocao = useEscritaDeCatalogo(
    (id: string) => apagarOperador(id),
    () => setConfirmando(null),
  );

  const ocupado = atividade.mutacao.isPending || remocao.mutacao.isPending;

  return (
    <>
      <CascaDeCatalogo
        titulo="Operadores"
        resumo="Os usuários internos que atendem chamados. O convite chega por e-mail após o cadastro."
        rotuloDoBotao="Novo operador"
        aoCriar={() => setCriando(true)}
        consulta={consulta}
        vazio="Nenhum operador cadastrado."
      >
        <LinhaDeCabecalho>
          <Cabecalho>Nome</Cabecalho>
          <Cabecalho>E-mail</Cabecalho>
          <Cabecalho>Cargo</Cabecalho>
          <Cabecalho>Equipes</Cabecalho>
          <Cabecalho>Último acesso</Cabecalho>
          <Cabecalho>Estado</Cabecalho>
          <Cabecalho className="text-right">Ações</Cabecalho>
        </LinhaDeCabecalho>

        <tbody>
          {operadores.map((op) => (
            <Linha key={op.id} inativo={op.status !== 'ACTIVE'}>
              <Celula className="font-medium">
                {op.displayName ?? op.name}
                {op.displayName ? (
                  <span className="block text-xs text-muted-foreground">{op.name}</span>
                ) : null}
              </Celula>
              <Celula className="text-xs">{op.email}</Celula>
              <Celula className="text-xs text-muted-foreground">
                {op.jobTitle ?? <span aria-label="sem cargo">—</span>}
              </Celula>
              <Celula className="max-w-[200px]">
                {op.teams.length === 0 ? (
                  <span className="text-xs text-muted-foreground" aria-label="sem equipe">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {op.teams.map((t) => (
                      <span
                        key={t.id}
                        title={t.name}
                        className="inline-flex items-center rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-foreground/80 ring-1 ring-inset ring-border"
                      >
                        {t.name}
                      </span>
                    ))}
                  </div>
                )}
              </Celula>
              <Celula className="text-xs text-muted-foreground">
                {op.lastLoginAt
                  ? new Date(op.lastLoginAt).toLocaleDateString('pt-BR')
                  : <span aria-label="nunca acessou">—</span>}
              </Celula>
              <Celula>
                {op.status === 'PENDING_APPROVAL' ? (
                  <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-semibold text-amber-600 ring-1 ring-inset ring-amber-500/30 dark:text-amber-400">
                    Aguardando aprovação
                  </span>
                ) : (
                  <EtiquetaDeAtividade ativo={op.status === 'ACTIVE'} />
                )}
              </Celula>
              <Celula>
                <AcoesDaLinha
                  registro={{ id: op.id, key: null, name: op.displayName ?? op.name, active: op.status === 'ACTIVE', version: op.version, usage: { tickets: 0, children: 0 } }}
                  entidade="operador"
                  ocupado={ocupado}
                  aoEditar={() => setEditando(op)}
                  aoAlternarAtividade={() => {
                    if (op.status !== 'ACTIVE') {
                      atividade.mutacao.mutate({ id: op.id, status: 'ACTIVE' as const, version: op.version });
                      return;
                    }
                    setConfirmando({ registro: op, intencao: 'desativar' });
                  }}
                  aoRemover={() => setConfirmando({ registro: op, intencao: 'remover' })}
                />
              </Celula>
            </Linha>
          ))}
        </tbody>
      </CascaDeCatalogo>

      <DialogoDeOperador
        aberto={criando || editando !== null}
        operador={editando}
        aoFechar={() => {
          setCriando(false);
          setEditando(null);
        }}
      />

      <ConfirmacaoDeCatalogo
        aberto={confirmando !== null}
        intencao={confirmando?.intencao ?? 'desativar'}
        entidade="operador"
        registro={
          confirmando
            ? { id: confirmando.registro.id, key: null, name: confirmando.registro.displayName ?? confirmando.registro.name, active: confirmando.registro.status === 'ACTIVE', version: confirmando.registro.version, usage: { tickets: 0, children: 0 } }
            : null
        }
        erro={atividade.erro ?? remocao.erro}
        ocupado={ocupado}
        aoFechar={() => setConfirmando(null)}
        aoConfirmar={() => {
          if (!confirmando) return;
          if (confirmando.intencao === 'remover') {
            remocao.mutacao.mutate(confirmando.registro.id);
            return;
          }
          atividade.mutacao.mutate({
            id: confirmando.registro.id,
            status: 'DEACTIVATED',
            version: confirmando.registro.version,
          });
        }}
      />
    </>
  );
}

function DialogoDeOperador({
  aberto,
  operador,
  aoFechar,
}: {
  aberto: boolean;
  operador: AdminUser | null;
  aoFechar: () => void;
}) {
  const editando = operador !== null;
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [nomeExibicao, setNomeExibicao] = useState('');
  const [cargo, setCargo] = useState('');
  const [telefone, setTelefone] = useState('');
  const [semeado, setSemeado] = useState<string | null>(null);
  const [papelSelecionado, setPapelSelecionado] = useState('');
  const [erroPapel, setErroPapel] = useState<string | null>(null);

  const { data: papeis } = useQuery({
    queryKey: chavesDeAdministracao.papeis,
    queryFn: listarPapeis,
    enabled: editando && aberto,
  });

  const atribuir = useMutation({
    mutationFn: ({ roleId }: { roleId: string }) => atribuirPapel(operador!.id, roleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeAdministracao.operadores });
      setPapelSelecionado('');
      setErroPapel(null);
    },
    onError: (e: Error) => setErroPapel(e.message),
  });

  const revogar = useMutation({
    mutationFn: ({ assignmentId }: { assignmentId: string }) =>
      revogarPapel(operador!.id, assignmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeAdministracao.operadores });
    },
  });

  const identidade = operador?.id ?? (aberto ? 'novo' : null);
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setEmail(operador?.email ?? '');
    setNome(operador?.name ?? '');
    setNomeExibicao(operador?.displayName ?? '');
    setCargo(operador?.jobTitle ?? '');
    setTelefone(operador?.phone ?? '');
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCatalogo(async () => {
    if (operador) {
      return alterarOperador(operador.id, {
        name: nome.trim(),
        displayName: nomeExibicao.trim() || undefined,
        jobTitle: cargo.trim() || undefined,
        phone: telefone.trim() || undefined,
        version: operador.version,
      });
    }
    return criarOperador({
      email: email.trim(),
      name: nome.trim(),
      displayName: nomeExibicao.trim() || undefined,
      jobTitle: cargo.trim() || undefined,
      phone: telefone.trim() || undefined,
    });
  }, aoFechar);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={editando ? `Editar "${operador.displayName ?? operador.name}"` : 'Novo operador'}
      descricao={
        editando
          ? 'O e-mail não pode ser alterado aqui. Use a gestão de identidade para isso.'
          : 'O operador receberá um convite por e-mail para definir sua senha.'
      }
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        {!editando && (
          <Campo id="op-email" rotulo="E-mail">
            <input
              id="op-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={320}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="op-nome" rotulo="Nome completo">
            <input
              id="op-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              minLength={2}
              maxLength={100}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>

          <Campo id="op-nome-exibicao" rotulo="Nome de exibição" opcional>
            <input
              id="op-nome-exibicao"
              value={nomeExibicao}
              onChange={(e) => setNomeExibicao(e.target.value)}
              maxLength={100}
              placeholder="Como aparece nos chamados"
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="op-cargo" rotulo="Cargo" opcional>
            <input
              id="op-cargo"
              value={cargo}
              onChange={(e) => setCargo(e.target.value)}
              maxLength={100}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>

          <Campo id="op-telefone" rotulo="Telefone" opcional>
            <input
              id="op-telefone"
              type="tel"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              maxLength={40}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        </div>

        {editando && (
          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-sm font-medium">Papéis (escopo global)</p>
            <div className="flex flex-wrap gap-1.5 min-h-[28px]">
              {operador.roles.length === 0 ? (
                <span className="text-xs text-muted-foreground">Nenhum papel atribuído.</span>
              ) : (
                operador.roles.map((r) => (
                  <span
                    key={r.id}
                    className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-border"
                  >
                    {r.roleName}
                    <button
                      type="button"
                      aria-label={`Remover papel ${r.roleName}`}
                      disabled={revogar.isPending}
                      onClick={() => revogar.mutate({ assignmentId: r.id })}
                      className="ml-0.5 rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none"
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>

            {papeis && (
              <div className="flex gap-2">
                <select
                  value={papelSelecionado}
                  onChange={(e) => setPapelSelecionado(e.target.value)}
                  className={`${CLASSE_DE_CAMPO} flex-1`}
                >
                  <option value="">Adicionar papel…</option>
                  {papeis.items
                    .filter((p) => !operador.roles.some((r) => r.roleId === p.id))
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </select>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!papelSelecionado || atribuir.isPending}
                  loading={atribuir.isPending}
                  onClick={() => papelSelecionado && atribuir.mutate({ roleId: papelSelecionado })}
                >
                  Adicionar
                </Button>
              </div>
            )}
            {erroPapel ? (
              <p role="alert" className="text-xs text-destructive">{erroPapel}</p>
            ) : null}
          </div>
        )}

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            {editando ? 'Salvar' : 'Criar operador'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Silencia o `no-unused-vars` em builds que não referenciam este mapa
void STATUS_ROTULO;
